import { ClassroomSummary } from './../../classroom360/classroom-summary/models/classroom-summary.model';
import { ClassroomSummaryService } from './../../classroom360/classroom-summary/classroom-summary.service';
import { ProfileAttachment } from './profile-attachment.model';
import { Profile } from './profile.model';
import { YearService } from './../../shared-components/year/year.service';
import { UserService } from './../../shared-components/user/user.service';
import { flatMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { System } from './../../shared-components/System';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

@Injectable()
export class ProfileService {

    // Properties
    classesWithProfiles: number[];

    // Class Functions
    constructor(
        private http: HttpClient,
        private userService: UserService,
        private classroomSummaryService: ClassroomSummaryService,
        private yearService: YearService
    ) { }

    // Methods
    getClassInformation(classId: number, useRelatedTopic = true): Observable<Profile[]> {
        // PRE: a class ID to check for
        // POST: if classes with profiles hasn't been checked, obtains it. Then, checks that the class has profile. If it does, obtains it.
        if (this.classesWithProfiles && this.classesWithProfiles.length != 0) {
            this.getClassProfile(classId, useRelatedTopic);
        }
        return this.getClassesWithProfiles().pipe(
           flatMap(coursesWithProfiles => {
               this.classesWithProfiles = coursesWithProfiles;
               return this.getClassProfile(classId, useRelatedTopic);
           })
        );
    }

    getClassProfile(classId: number, useRelatedTopic = true) {
        // PRE: a class ID to check for
        // POST: checks that a profile exists for that class. If it does, obtains its profile.
        const hasProfile = this.classesWithProfiles.find(course => course == classId) != undefined;
        if (hasProfile) {
             return this.getProfileContent(classId, true, useRelatedTopic);
        } else {
            return of([]);
        }
    }

    getClassesWithProfiles(): Observable<number[]> {
        // POST: obtains the classes with profiles by SSEC_ID
        return this.http.get<number[]>(System.apiURL.Student + 'GetIdsForClassesWithProfiles');
    }

    getAllClassTopics(): Observable<string[]> {
        // POST: obtains all the class topics
        const queryString = '?yearId=' + this.yearService.defaultYear.Id;
        return this.http.get<string[]>(System.apiURL.Student + 'GetAllClassTopics' + queryString);
    }

    getProfileContent(id: number, isCourseProfile = true, useRelatedTopic = true): Observable<Profile[]> {
        // PRE: a class ID to check for
        // POST: obtains the profile for that class
        const queryString = '?Id=' + id +
            '&IsCourseProfile=' + isCourseProfile +
            '&useRelatedTopic=' + useRelatedTopic;
        return this.http.get<Profile[]>(System.apiURL.Student + 'GetCourseAndTeacherProfileContent' + queryString);
    }

    updateProfileSequences(classInformation: Profile[]): Observable<any> {
        // PRE: a list of profiles to save
        // POST: saves the profiles to the server
        const queryString = '?userId=' + this.userService.currentUser.Id;

        // Syncing the data
        classInformation.forEach(info => {
            if (!info.ProfileAttachments) {
                info.Image = null;
                info.ProfileAttachments = [];
            }
            if (info.newSequence) {
                info.Sequence = info.newSequence;
            }
        });
        return this.http.post<any>(System.apiURL.Student + 'UpdateProfileSequences' + queryString, classInformation);
    }

    updateProfile(classInformation: Profile): Observable<any> {
        // PRE: a profile to update (ID can't be changed)
        // POST: updates the profile on the database
        return this.http.post<any>(System.apiURL.Student + 'UpdateProfile', classInformation);
    }

    addTopic(information: Profile): Observable<any> {
        // PRE: information to add as a topic
        // POST: adds the topic to the database
        return this.http.post<any>(System.apiURL.Student + 'AddProfileTopic', information);
    }

    addProfile(information: Profile): Observable<Profile> {
        // PRE: information to add as a profile
        // POST: adds the profile to the database
        return this.http.post<Profile>(System.apiURL.Student + 'AddProfileContent', information);
    }

    setRelatedTopic(parentCourse: ClassroomSummary = null): Observable<any> {
        // PRE: A parent course that CAN be null
        // POST: sets this course as a related topic to the parent course
        const queryString = '?userId=' + this.userService.currentUser.Id +
            '&childSSECID=' + this.classroomSummaryService.selectedCourse.SSEC_ID +
            '&parentSSECId=' + (parentCourse ? parentCourse.SSEC_ID : -1);
        return this.http.get<any>(System.apiURL.Student + 'SetRelatedTopic' + queryString);
    }

    updateProfileAttachment(attachment: ProfileAttachment): Observable<number> {
        // PRE: an attachment to update
        // POST: updates that attachment on the server
        return this.http.post<number>(System.apiURL.Student + 'UpdateProfileAttachment', attachment);
    }

    deleteProfileAttachment(attachmentId: number): Observable<any> {
        // PRE: an attachment to delete
        // POST: deletes the attachment on the server
        const queryString = '?attachmentId=' + attachmentId;
        return this.http.get<any>(System.apiURL.Student + 'DeleteProfileAttachment' + queryString);
    }

    copyClassProfile(sourceId: number, destinationId: number): Observable<Profile[]> {
        // PRE: a source to copy from and a destination to copy to
        // POST: copies from the source to the destination
        const queryString = '?srcId=' + sourceId +
            '&destId=' + destinationId;
        return this.http.get<Profile[]>(System.apiURL.Student + 'CopyClassProfile' + queryString);
    }

    getAttachmentFile(fileId: number): Observable<ProfileAttachment> {
        // PRE: a file id
        // POST: obtains that attachment
        const queryString = '?fileId=' + fileId;
        return this.http.get<ProfileAttachment>(System.apiURL.Student + 'getAttachmentFile' + queryString);
    }
}
